import React from 'react';
import { useForm } from 'react-hook-form';

import { useAuth } from '../../../features/auth/AuthContext';
import { getSimpleISO } from '../../../shared/lib/helpers';
import Modal from '../../../shared/ui/Modal';
import {
    createShop,
    deleteShop,
    fetchShop,
    fetchShopList,
    updateShop,
} from '../shops.api';

interface ShopFormInputs {
    market_site: string;
    name: string;
    api_key: string;
    api_secret: string;
    seller_center_id: string;
    seller_center_pw: string;
    url?: string;
}

interface ShopData extends ShopFormInputs {
    id: string;
    url?: string;
    tested_at: string | null;
}

const marketNames: Record<string, string> = {
    smartstore: '스마트스토어',
    auction: '옥션',
    gmarket: '지마켓',
    eleventh: '11번가',
    lotteon: '롯데ON',
    ssgdotcom: 'SSG닷컴',
};

const defaultValues: ShopFormInputs = {
    market_site: 'smartstore',
    name: '',
    api_key: '',
    api_secret: '',
    seller_center_id: '',
    seller_center_pw: '',
    url: '',
};

const ShopApiSettingsForm: React.FC = () => {
    const auth = useAuth();
    const [shops, setShops] = React.useState<ShopData[]>([]);
    const [isLoading, setIsLoading] = React.useState(false);
    const [errorMessage, setErrorMessage] = React.useState<string | null>(null);
    const [processMessage, setProcessMessage] = React.useState<string | null>(null);
    const [editingId, setEditingId] = React.useState<string | null>(null);
    const [showForm, setShowForm] = React.useState(false);
    const [accessKeyLabel, setAccessKeyLabel] = React.useState('액세스키');
    const [secretKeyLabel, setSecretKeyLabel] = React.useState('시크릿키');

    const {
        register,
        handleSubmit,
        reset,
        setError,
        clearErrors,
        setValue,
        watch,
        formState: { errors },
    } = useForm<ShopFormInputs>({ defaultValues });
    const watchedMarketSite = watch('market_site');
    const [marketSite, setMarketSite] = React.useState('smartstore');
    const previousMarketSite = React.useRef('');

    const loadShops = React.useCallback(() => {
        setIsLoading(true);
        fetchShopList({
            auth,
            setErrorMessage,
            setResult: (data) => setShops(Array.isArray(data) ? data : []),
        }).finally(() => setIsLoading(false));
    }, [auth]);

    React.useEffect(() => {
        loadShops();
    }, [loadShops]);

    React.useEffect(() => {
        setMarketSite(watchedMarketSite || 'smartstore');
    }, [watchedMarketSite]);

    React.useEffect(() => {
        const selectedMarketSite = marketSite || 'smartstore';
        if (selectedMarketSite === 'smartstore') {
            setAccessKeyLabel('액세스키');
            setSecretKeyLabel('시크릿키');
        } else if (selectedMarketSite === 'auction' || selectedMarketSite === 'gmarket') {
            setAccessKeyLabel('상점ID');
            setSecretKeyLabel('시크릿키');
        } else if (selectedMarketSite === 'lotteon' || selectedMarketSite === 'ssgdotcom') {
            setAccessKeyLabel('오픈API키');
            setSecretKeyLabel('시크릿키');
        } else {
            setAccessKeyLabel('사용자ID');
            setSecretKeyLabel('오픈API키');
        }

        if (
            previousMarketSite.current &&
            marketSite &&
            previousMarketSite.current !== marketSite
        ) {
            setValue('api_key', '');
            setValue('api_secret', '');
            setValue('url', '');
            setValue('seller_center_id', '');
            setValue('seller_center_pw', '');
            clearErrors(['api_key', 'api_secret', 'url', 'seller_center_id', 'seller_center_pw']);
        }
        previousMarketSite.current = marketSite || '';
    }, [marketSite, clearErrors, setValue]);

    const handleEdit = async (id: string) => {
        setErrorMessage(null);
        await fetchShop({
            id,
            auth,
            setErrorMessage,
            setResult: (data) => {
                previousMarketSite.current = data.market_site || '';
                reset(data);
                setEditingId(id);
                setShowForm(true);
            },
        });
    };

    const resetForm = () => {
        reset(defaultValues);
        setEditingId(null);
        setErrorMessage(null);
        setShowForm(false);
    };

    const handleAdd = () => {
        resetForm();
        setShowForm(true);
    };

    const onSubmit = async (data: ShopFormInputs) => {
        const isEsmMarket = data.market_site === 'auction' || data.market_site === 'gmarket';
        const isEleventhMarket = data.market_site === 'eleventh';
        const payload = {
            market_site: data.market_site,
            name: data.name,
            api_key: isEleventhMarket ? '' : data.api_key,
            api_secret: isEsmMarket || (data.market_site === 'lotteon' || data.market_site === 'ssgdotcom') ? '' : data.api_secret,
            seller_center_id: data.market_site === 'smartstore' || data.market_site === 'lotteon' ? data.seller_center_id : '',
            seller_center_pw: data.market_site === 'smartstore' || data.market_site === 'lotteon' ? data.seller_center_pw : '',
            url: data.market_site === 'smartstore' ? data.url : '',
        };

        setErrorMessage(null);
        setProcessMessage('API 접속을 테스트하고 있습니다...');
        if (editingId) {
            await updateShop({
                id: editingId,
                auth,
                payload,
                setErrorMessage,
                setProcessMessage,
                setError,
                setResult: () => {
                    loadShops();
                    resetForm();
                },
            });
        } else {
            await createShop({
                auth,
                payload,
                setErrorMessage,
                setProcessMessage,
                setError,
                setResult: () => {
                    loadShops();
                    resetForm();
                },
            });
        }
    };

    const handleDelete = async (id: string) => {
        if (!confirm('삭제하시겠습니까?')) return;
        setErrorMessage(null);
        await deleteShop({
            auth,
            id,
            setErrorMessage,
            setProcessMessage,
            setResult: () => {
                loadShops();
                if (editingId === id) {
                    resetForm();
                }
            },
        });
    };

    const isEsmMarket = marketSite === 'auction' || marketSite === 'gmarket';
    const isEleventhMarket = marketSite === 'eleventh';
    const isLotteonMarket = marketSite === 'lotteon';
    const isSmartstoreMarket = marketSite === 'smartstore';

    return (<>
        {errorMessage && !showForm && (
            <div className="alert alert-danger" role="alert">
                {errorMessage}
            </div>
        )}
        {processMessage && !showForm && (
            <div className="alert alert-info" role="alert">
                {processMessage}
            </div>
        )}

        <div className="d-flex justify-content-between align-items-center mb-1">
            <div>상점 API 설정 목록</div>
            <button type="button" className="btn btn-primary" onClick={handleAdd}>추가</button>
        </div>
        <table className="table text-center">
                    <thead>
                        <tr>
                            <th className="fw-normal">#</th>
                            <th className="fw-normal">상점명</th>
                            <th className="fw-normal">마켓</th>
                            <th className="fw-normal">연결확인</th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        {isLoading ? (
                            <tr>
                                <td colSpan={5} className="text-center align-middle" style={{ height: '5rem' }}>
                                    로딩 중입니다.
                                </td>
                            </tr>
                        ) : shops.length === 0 ? (
                            <tr>
                                <td colSpan={5} className="text-center align-middle" style={{ height: '5rem' }}>
                                    등록된 상점 API 설정이 없습니다.
                                </td>
                            </tr>
                        ) : (
                            shops.map((shop, idx) => (
                                <tr key={shop.id}>
                                    <td>{idx + 1}</td>
                                    <td>{shop.name}</td>
                                    <td>{marketNames[shop.market_site] || shop.market_site}</td>
                                    <td title={shop.tested_at ? getSimpleISO(shop.tested_at) : undefined}>
                                        {shop.tested_at ? '검증' : '미확인'}
                                    </td>
                                    <td>
                                        <button
                                            type="button"
                                            className="btn btn-sm btn-outline-primary"
                                            onClick={() => handleEdit(shop.id)}
                                        >
                                            수정
                                        </button>
                                        <button
                                            type="button"
                                            className="btn btn-sm btn-outline-danger ms-1"
                                            onClick={() => handleDelete(shop.id)}
                                        >
                                            삭제
                                        </button>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
        </table>
        <Modal show={showForm} onHide={resetForm} size="lg" scrollable>
            <Modal.Header closeButton>
                <Modal.Title>{editingId ? '상점 API 설정 수정' : '상점 API 설정 추가'}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {errorMessage && (
                    <div className="alert alert-danger" role="alert">{errorMessage}</div>
                )}
                {processMessage && (
                    <div className="alert alert-info" role="alert">{processMessage}</div>
                )}
                <form onSubmit={handleSubmit(onSubmit)} noValidate>
                    <ul className="list-unstyled mt-2">
                        <li className="mb-3">
                            <label htmlFor="id_shop_market_site" className="form-label">마켓</label>
                            <select
                                id="id_shop_market_site"
                                className={`form-select ${errors.market_site ? 'is-invalid' : ''}`}
                                {...register('market_site', { required: '마켓을 선택하세요.' })}
                            >
                                <option value="smartstore">스마트스토어</option>
                                <option value="auction">옥션</option>
                                <option value="gmarket">지마켓</option>
                                <option value="eleventh">11번가</option>
                                <option value="lotteon">롯데ON</option>
                                <option value="ssgdotcom">SSG닷컴</option>
                            </select>
                            {errors.market_site && <div className="invalid-feedback">{errors.market_site.message}</div>}
                        </li>
                        <li className="mb-3">
                            <label htmlFor="id_shop_name" className="form-label">상점명</label>
                            <input
                                type="text"
                                id="id_shop_name"
                                className={`form-control ${errors.name ? 'is-invalid' : ''}`}
                                {...register('name', { required: '상점명을 입력하세요.', minLength: 1 })}
                            />
                            {errors.name && <div className="invalid-feedback">{errors.name.message}</div>}
                        </li>
                        {!isEleventhMarket && (
                            <li className="mb-3">
                                <label htmlFor="id_shop_api_key" className="form-label">
                                    {accessKeyLabel}
                                </label>
                                <input
                                    type="text"
                                    id="id_shop_api_key"
                                    className={`form-control ${errors.api_key ? 'is-invalid' : ''}`}
                                    {...register('api_key', {
                                        required: `${accessKeyLabel}를 입력하세요.`,
                                        minLength: { value: 4, message: `${accessKeyLabel}는 4자 이상 입력하세요.` },
                                    })}
                                />
                                {errors.api_key && <div className="invalid-feedback">{errors.api_key.message}</div>}
                            </li>
                        )}
                        {!isEsmMarket && !isLotteonMarket && marketSite !== 'ssgdotcom' && (
                            <li className="mb-3">
                                <label htmlFor="id_shop_api_secret" className="form-label">
                                    {secretKeyLabel}
                                </label>
                                <input
                                    type="text"
                                    id="id_shop_api_secret"
                                    className={`form-control ${errors.api_secret ? 'is-invalid' : ''}`}
                                    {...register('api_secret', {
                                        required: `${secretKeyLabel}를 입력하세요.`,
                                        minLength: { value: 4, message: `${secretKeyLabel}는 4자 이상 입력하세요.` },
                                    })}
                                />
                                {errors.api_secret && <div className="invalid-feedback">{errors.api_secret.message}</div>}
                            </li>
                        )}
                        {(isSmartstoreMarket || isLotteonMarket) && (
                            <>
                                <li className="mb-3">
                                    <label htmlFor="id_shop_seller_center_id" className="form-label">판매자센터ID</label>
                                    <input
                                        type="text"
                                        id="id_shop_seller_center_id"
                                        className={`form-control ${errors.seller_center_id ? 'is-invalid' : ''}`}
                                        {...register('seller_center_id', { required: isSmartstoreMarket && '판매자센터ID를 입력하세요.' })}
                                    />
                                    {errors.seller_center_id && <div className="invalid-feedback">{errors.seller_center_id.message}</div>}
                                </li>
                                <li className="mb-3">
                                    <label htmlFor="id_shop_seller_center_pw" className="form-label">판매자센터PW</label>
                                    <input
                                        type="password"
                                        id="id_shop_seller_center_pw"
                                        className={`form-control ${errors.seller_center_pw ? 'is-invalid' : ''}`}
                                        {...register('seller_center_pw', { required: isSmartstoreMarket && '판매자센터PW를 입력하세요.' })}
                                    />
                                    {errors.seller_center_pw && <div className="invalid-feedback">{errors.seller_center_pw.message}</div>}
                                </li>
                            </>
                        )}
                        {isSmartstoreMarket && (
                            <li className="mb-3">
                                <label htmlFor="id_shop_url" className="form-label">
                                    상점 URL (목록 내 상품 즉시 조회용)
                                </label>
                                <input
                                    type="text"
                                    id="id_shop_url"
                                    className={`form-control ${errors.url ? 'is-invalid' : ''}`}
                                    {...register('url', {
                                        validate: (value) => !value || value.length >= 4 || 'URL은 4자 이상 입력하세요.',
                                    })}
                                />
                                {errors.url && <div className="invalid-feedback">{errors.url.message}</div>}
                            </li>
                        )}
                        <li className="d-flex gap-2">
                            <button type="submit" className="btn btn-primary">
                                {editingId ? '변경' : '추가'}
                            </button>
                            <button type="button" className="btn btn-outline-secondary" onClick={resetForm}>
                                취소
                            </button>
                        </li>
                    </ul>
                </form>
            </Modal.Body>
        </Modal>
    </>);
};

export default ShopApiSettingsForm;
